import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'daynote-onboarding-done';

interface Props {
  isMobile?: boolean;
}

export function OnboardingTour({ isMobile }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<number | null>(null);

  const steps = t('onboarding.steps', { returnObjects: true }) as Array<{
    title: string;
    mobileTitle: string;
    desc: string;
  }>;

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setStep(0);
    }
  }, []);

  if (step === null) return null;

  const current = steps[step]!;
  const isLast = step === steps.length - 1;

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setStep(null);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end md:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative w-full max-w-sm bg-[var(--panel-bg)] backdrop-blur-2xl border border-[var(--grid-line)] rounded-2xl p-6 shadow-2xl">
        <div className="text-[10px] tracking-[0.12em] uppercase text-daynote-accent mb-2">
          {t('onboarding.title')} {t('onboarding.step', { current: step + 1, total: steps.length })}
        </div>
        <h3 className="font-display text-xl mb-2">
          {isMobile && current.mobileTitle ? current.mobileTitle : current.title}
        </h3>
        <p className="text-sm text-daynote-muted leading-relaxed mb-6">{current.desc}</p>
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={finish}
            className="text-xs text-daynote-muted bg-transparent border-none cursor-pointer hover:text-daynote-text"
          >
            {t('onboarding.skip')}
          </button>
          <button
            type="button"
            onClick={() => (isLast ? finish() : setStep(step + 1))}
            className="px-5 py-2 text-sm bg-daynote-accent text-[#2a2520] rounded-lg font-medium border-none cursor-pointer"
          >
            {isLast ? t('onboarding.start') : t('onboarding.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
